import { motion } from "framer-motion";
import { UserPlus, Zap, Heart, DollarSign, Lightbulb } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export function FlywheelSection() {
  const { t } = useLanguage();

  const flywheelSteps = [
    { name: t("flywheel.step.acquisition"), icon: UserPlus, angle: -72 },
    { name: t("flywheel.step.activation"), icon: Zap, angle: 0 },
    { name: t("flywheel.step.retention"), icon: Heart, angle: 72 },
    { name: t("flywheel.step.revenue"), icon: DollarSign, angle: 144 },
    { name: t("flywheel.step.learning"), icon: Lightbulb, angle: 216 },
  ];

  return (
    <section id="growth-system" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px]" />
      </div>

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("flywheel.title")}{" "}
            <span className="text-primary">{t("flywheel.titleHighlight")}</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Flywheel visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
              {/* Outer ring */}
              <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
              <div className="absolute inset-4 rounded-full border border-primary/10" />
              
              {/* Center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-primary" />
                  </div>
                </motion.div>
              </div>

              {/* Flywheel items */}
              {flywheelSteps.map((step, index) => {
                const radius = 140;
                const angleRad = (step.angle * Math.PI) / 180;
                const x = Math.cos(angleRad) * radius;
                const y = Math.sin(angleRad) * radius;

                return (
                  <motion.div
                    key={step.name}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="absolute"
                    style={{
                      top: '50%',
                      left: '50%',
                      x: x,
                      y: y,
                      translateX: '-50%',
                      translateY: '-50%',
                    }}
                  >
                    <div className="card-surface p-3 flex flex-col items-center gap-2 hover-glow cursor-default">
                      <div className="icon-box-sm">
                        <step.icon className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-medium text-foreground whitespace-nowrap">
                        {step.name}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="card-surface p-6">
              <p className="text-lg text-foreground leading-relaxed">
                {t("flywheel.card.1")} <span className="text-primary font-semibold">{t("flywheel.card.1.highlight")}</span>.
              </p>
            </div>
            <div className="card-surface p-6">
              <p className="text-lg text-foreground leading-relaxed">
                {t("flywheel.card.2")} <span className="text-destructive font-semibold">{t("flywheel.card.2.highlight")}</span>.
              </p>
            </div>
            <p className="text-muted-foreground">
              {t("flywheel.description")}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
